import React, { useEffect, useState, useCallback, useMemo } from 'react';
import AdminLayout from '../components/AdminLayout';
import api from '../lib/api';
import { RefreshCw, Download, Plus, Search, Upload, Archive, MailWarning, X } from 'lucide-react';
import { toast } from 'sonner';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function StatusPill({ status }) {
  const tones = {
    lead: 'border-[#d8b34a]/40 text-[#d8b34a] bg-[#d8b34a]/5',
    invited: 'border-amber-500/40 text-amber-400 bg-amber-500/5',
    interested: 'border-blue-500/40 text-blue-400 bg-blue-500/5',
    converted: 'border-emerald-500/40 text-emerald-400 bg-emerald-500/5',
    unsubscribed: 'border-zinc-500/40 text-zinc-400',
    bounced: 'border-red-500/40 text-red-400 bg-red-500/5',
  };
  return <span className={`text-[10px] font-mono px-2 py-1 border rounded-sm ${tones[status] || 'border-white/10 text-zinc-400'}`}>{(status || '—').toUpperCase()}</span>;
}

const FILTERS = [
  { key: 'all', label: 'All', params: {} },
  { key: 'lead', label: 'Lead', params: { contact_status: 'lead' } },
  { key: 'invited', label: 'Invited', params: { contact_status: 'invited' } },
  { key: 'converted', label: 'Converted', params: { contact_status: 'converted' } },
  { key: 'unsubscribed', label: 'Unsubscribed', params: { contact_status: 'unsubscribed' } },
  { key: 'archived', label: 'Archived', params: { archived: true } },
];

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...(FILTERS.find(f => f.key === filter)?.params || {}) };
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get('/admin/contacts', { params });
      setContacts(data.items || []);
    } catch (e) {
      console.error('[admin/contacts] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const exportUrl = useMemo(() => `${api.defaults.baseURL}/admin/contacts/export`, []);

  const handleArchive = async (c) => {
    if (!window.confirm(`Archive ${c.email}?`)) return;
    try {
      await api.delete(`/admin/contacts/${c.id}`);
      toast.success('Archived.');
      load();
    } catch (e) {
      toast.error('Archive failed.');
    }
  };

  const handleToggleMarketing = async (c) => {
    try {
      await api.patch(`/admin/contacts/${c.id}`, { marketing_emails_enabled: !c.marketing_emails_enabled });
      toast.success('Updated.');
      load();
    } catch (e) {
      toast.error('Update failed.');
    }
  };

  return (
    <AdminLayout title="Contacts" description="Newsletter list / leads / beta-invite list. These are NOT app users — they have no dashboard or Alpaca access.">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="search"
              placeholder="Search by email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="contacts-search"
              className="pl-9 pr-3 py-2 w-64 border border-white/10 rounded-sm bg-[#0c0c0e] text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-[#d8b34a]/50"
            />
          </div>
          <p className="text-sm text-zinc-500 font-mono ml-2">{loading ? '…' : `${contacts.length}`}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowAdd(true)} data-testid="contacts-add-single-btn" className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-white/10 text-zinc-300 hover:border-[#d8b34a]/50 hover:text-white rounded-sm">
            <Plus size={13} /> Add contact
          </button>
          <button onClick={() => setShowImport(true)} data-testid="contacts-import-btn" className="inline-flex items-center gap-2 text-sm px-4 py-2 border gold-border gold-text hover:bg-[#d8b34a] hover:text-black rounded-sm font-medium">
            <Upload size={13} /> Bulk import
          </button>
          <a href={exportUrl} data-testid="contacts-export-btn" className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-white/10 text-zinc-300 hover:border-[#d8b34a]/50 hover:text-white rounded-sm">
            <Download size={13} /> Export CSV
          </a>
          <button onClick={load} data-testid="contacts-refresh" className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-white/10 text-zinc-300 hover:border-[#d8b34a]/50 hover:text-white rounded-sm">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-5" data-testid="contacts-filters">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`contacts-filter-${f.key}`}
            className={`text-xs px-3 py-1.5 border rounded-sm transition-all font-mono ${
              filter === f.key ? 'border-[#d8b34a]/50 text-white bg-[#d8b34a]/[0.06]' : 'border-white/10 text-zinc-400 hover:text-white'
            }`}
          >{f.label.toUpperCase()}</button>
        ))}
      </div>

      <div className="border border-white/10 rounded-md overflow-hidden bg-[#0c0c0e]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] border-b border-white/10">
              <tr className="text-left text-[10px] tracking-[0.3em] font-mono text-zinc-500">
                <th className="px-4 py-3">EMAIL</th>
                <th className="px-4 py-3">STATUS</th>
                <th className="px-4 py-3">SOURCE</th>
                <th className="px-4 py-3">MARKETING</th>
                <th className="px-4 py-3">LAST EMAIL</th>
                <th className="px-4 py-3">ADDED</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="contacts-tbody">
              {contacts.map(c => (
                <tr key={c.id} className={`border-t border-white/5 hover:bg-white/[0.02] ${c.archived ? 'opacity-50' : ''}`} data-testid={`contact-row-${c.email}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-100">{c.email}</span>
                      {c.converted_user_id && <span className="text-[10px] gold-text font-mono">USER</span>}
                    </div>
                    {c.name && <p className="text-xs text-zinc-500 mt-0.5">{c.name}</p>}
                  </td>
                  <td className="px-4 py-3"><StatusPill status={c.contact_status} /></td>
                  <td className="px-4 py-3 text-xs text-zinc-400 font-mono">{c.source}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleToggleMarketing(c)} data-testid={`contact-marketing-${c.email}`} className={`text-[10px] font-mono px-2 py-1 border rounded-sm transition-colors ${
                      c.marketing_emails_enabled ? 'border-emerald-500/40 text-emerald-400 bg-emerald-500/5' : 'border-zinc-500/40 text-zinc-500'
                    }`}>
                      {c.marketing_emails_enabled ? 'OPTED-IN' : 'UNSUBSCRIBED'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500 font-mono">{fmtDate(c.last_email_sent_at)}</td>
                  <td className="px-4 py-3 text-xs text-zinc-500 font-mono whitespace-nowrap">{fmtDate(c.created_at)}</td>
                  <td className="px-4 py-3">
                    {!c.archived && (
                      <button onClick={() => handleArchive(c)} data-testid={`contact-archive-${c.email}`} className="text-xs px-2 py-1 border border-white/10 text-zinc-500 hover:text-red-400 hover:border-red-500/40 rounded-sm">
                        <Archive size={11} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && contacts.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-zinc-500">No contacts match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-6 text-xs text-zinc-500 leading-relaxed max-w-2xl">
        <MailWarning size={11} className="inline gold-text mr-1" />
        Contacts cannot sign in — they only become app users after clicking a single-use invite link sent via the <a href="/admin/email" className="gold-text underline">Email Center</a>.
      </p>

      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
      {showAdd && <AddModal onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); }} />}
    </AdminLayout>
  );
}

function ImportModal({ onClose, onDone }) {
  const [text, setText] = useState('');
  const [source, setSource] = useState('import');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const handleImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/contacts/import', { emails: text, source });
      setResult(data);
      toast.success(`${data.summary.created_count} created, ${data.summary.already_existed_count} duplicates skipped.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="import-contacts-modal">
      <div className="w-full max-w-xl border border-white/10 rounded-md bg-[#0c0c0e] p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[10px] tracking-[0.3em] font-mono gold-text">BULK IMPORT</p>
            <h3 className="font-display mt-1 text-xl font-bold text-white">Add contacts</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        {!result ? (
          <>
            <label className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">PASTE EMAILS (one per line, comma-separated, or whitespace-separated)</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="import-textarea"
              rows={8}
              placeholder="alice@example.com&#10;bob@example.com, carol@example.com&#10;..."
              className="mt-2 w-full px-3 py-2 border border-white/10 rounded-sm bg-[#0a0a0b] text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-[#d8b34a]/50 font-mono resize-none"
            />
            <label className="text-[10px] tracking-[0.3em] font-mono text-zinc-500 mt-4 block">SOURCE TAG</label>
            <input
              type="text"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              data-testid="import-source"
              className="mt-2 w-full px-3 py-2 border border-white/10 rounded-sm bg-[#0a0a0b] text-sm text-zinc-100"
            />
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={onClose} className="text-sm px-4 py-2 border border-white/10 text-zinc-400 rounded-sm">Cancel</button>
              <button onClick={handleImport} disabled={busy || !text.trim()} data-testid="import-submit" className="text-sm px-4 py-2 border gold-border gold-text hover:bg-[#d8b34a] hover:text-black rounded-sm disabled:opacity-60">
                {busy ? 'Importing…' : 'Import'}
              </button>
            </div>
          </>
        ) : (
          <div data-testid="import-result">
            <p className="text-sm text-zinc-200 mb-4">Import complete:</p>
            <ul className="text-sm space-y-1.5">
              <li className="flex justify-between"><span className="text-zinc-400">Created</span><span className="text-emerald-400 font-mono">{result.summary.created_count}</span></li>
              <li className="flex justify-between"><span className="text-zinc-400">Already existed</span><span className="text-zinc-300 font-mono">{result.summary.already_existed_count}</span></li>
              <li className="flex justify-between"><span className="text-zinc-400">Already app users</span><span className="text-amber-400 font-mono">{result.summary.already_user_count}</span></li>
              <li className="flex justify-between"><span className="text-zinc-400">Invalid emails</span><span className="text-red-400 font-mono">{result.summary.invalid_count}</span></li>
            </ul>
            {result.invalid?.length > 0 && (
              <div className="mt-4">
                <p className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">REJECTED:</p>
                <p className="mt-2 text-xs text-zinc-400 font-mono break-all">{result.invalid.join(', ')}</p>
              </div>
            )}
            <div className="mt-5 flex justify-end">
              <button onClick={onDone} data-testid="import-done" className="text-sm px-4 py-2 border gold-border gold-text hover:bg-[#d8b34a] hover:text-black rounded-sm">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AddModal({ onClose, onDone }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/admin/contacts', { email, name, notes, source: 'manual' });
      toast.success('Contact added.');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="add-contact-modal">
      <div className="w-full max-w-md border border-white/10 rounded-md bg-[#0c0c0e] p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-bold text-white">Add contact</h3>
          <button onClick={onClose} className="text-zinc-500"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">EMAIL</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="add-contact-email" className="mt-2 w-full px-3 py-2 border border-white/10 rounded-sm bg-[#0a0a0b] text-sm text-zinc-100 focus:outline-none focus:border-[#d8b34a]/50" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">NAME (optional)</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 w-full px-3 py-2 border border-white/10 rounded-sm bg-[#0a0a0b] text-sm text-zinc-100" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">NOTES (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-2 w-full px-3 py-2 border border-white/10 rounded-sm bg-[#0a0a0b] text-sm text-zinc-100 resize-none" />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 border border-white/10 text-zinc-400 rounded-sm">Cancel</button>
          <button onClick={submit} disabled={busy || !email} data-testid="add-contact-submit" className="text-sm px-4 py-2 border gold-border gold-text hover:bg-[#d8b34a] hover:text-black rounded-sm disabled:opacity-60">
            {busy ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}
